import { Injectable } from "@angular/core";
import { AddNameContainer } from "../add-note/add-name/add-name";
import { AddThoughtsContainer } from "../add-note/add-thoughts/add-thoughts";
import { ChooseSongComponent } from "../add-note/choose-song/choose-song.component";
import { DescribeSecretionContainer } from "../add-note/describe-secretion/describe-secretion";
import { PainMeterContainer } from "../add-note/pain-meter/pain-meter";
import { RandomQuestionComponent } from "../add-note/random-question/random-question.component";
import { ReportCardComponent } from "../add-note/report-card/report-card.component";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { LocalStorageHelper } from "./local-storage";
import { PinAuthenticationService } from "../services/pin-authentication.service";
import { SignInService } from "../services/sign-in.service";

@Injectable({
	providedIn: 'root',
})
export class NavigationHelper {
	constructor(private location: Location,
		private localStorageHelper: LocalStorageHelper,
		private pinAuthenticationService: PinAuthenticationService,
		private signInService: SignInService
	) { }

	// Important, the index order of these identifiers must match the value in the respective class. Ex., Addname index is 0 and value is 0.
	public readonly componentMapping: [number, string][] =
		[[AddNameContainer.ComponentIdentifier, '/potty-world/add-note/add-name'],
		[ChooseSongComponent.ComponentIdentifier, '/potty-world/add-note/choose-song'],
		[PainMeterContainer.ComponentIdentifier, '/potty-world/add-note/pain-meter'],
		[DescribeSecretionContainer.ComponentIdentifier, '/potty-world/add-note/describe-secretion'],
		[AddThoughtsContainer.ComponentIdentifier, '/potty-world/add-note/add-thoughts'],
		[RandomQuestionComponent.ComponentIdentifier, '/potty-world/add-note/random-questions'],
		[ReportCardComponent.ComponentIdentifier, '/potty-world/add-note/report-card']]

	navigateToStartOfFlow() {
		let startOforder = this.getOrderOfComponents()[0];
		localStorage.setItem("currentNoteIndex", "0");
		return this.componentMapping[startOforder][1];
	}

	getNextComponentUrl() {
		let order = this.getOrderOfComponents();
		let currentIndex = +(localStorage.getItem("currentNoteIndex") || "0");
		let nextRoute: string = "";
		// If we are not at the end, navigate to the next.
		nextRoute = this.componentMapping[order[currentIndex + 1]][1];
		// Overwrite the index.
		localStorage.setItem("currentNoteIndex", `${++currentIndex}`);
		return nextRoute;
	}

	getPreviousComponentUrl() {
		let order = this.getOrderOfComponents();
		let currentIndex = +(localStorage.getItem("currentNoteIndex") || "0");
		let previousRoute: string = ""; 
		previousRoute = this.componentMapping[order[currentIndex - 1]][1];
		localStorage.setItem("currentNoteIndex", `${--currentIndex}`);
		return previousRoute;
	}

	isStartOfNoteFlow() {
		let currentIndex = +(localStorage.getItem("currentNoteIndex") || "0");
		return currentIndex === 0;
	}

	isEndOfNoteFlow() {
		let order = this.getOrderOfComponents();
		let currentIndex = +(localStorage.getItem("currentNoteIndex") || "0");
		return currentIndex >= order?.length - 1;
	}

	constructHostUrl(): string {
		return window.location.protocol + "//" + new URL(window.location.href).host;
	}

	addUrlRedirectQueryParam(): string {
		return encodeURIComponent(window.location.href);
	}

	getRedirectPathFromEncodedUrl(route: ActivatedRoute): string {
		return new URL(decodeURIComponent(route.snapshot.queryParams["redirectTo"])).pathname
	}

	hasRedirectQueryParam(route: ActivatedRoute): boolean {
		return route.snapshot.queryParams["redirectTo"] != null && route.snapshot.queryParams["redirectTo"] != "";
	}

	redirectToDiaryOwnerNotes(router: Router) {
		router.navigate(['/potty-world/view-notes'], {
			queryParams: { did: this.localStorageHelper.getUserOwnedDiaryId() }
		});
	}

	redirectToDiaryOwnerAfterDelete(router: Router) {
		router.navigate(['/potty-world/view-notes'], {
			queryParams: { did: this.localStorageHelper.getUserOwnedDiaryId(), reload: true }
		});
	}

	redirectToViewNotes(router: Router, diaryId: string) {
		router.navigate(['/potty-world/view-notes'], {
			queryParams: { did: diaryId }
		});
	}

	redirectToDiaryOwnerNote(router: Router) {
		router.navigate([`/potty-world/view-notes`], {
			queryParams: { createdNewNote: true, did: this.localStorageHelper.getUserOwnedDiaryId() }
		});
	}

	public hasCreatedNewNoteQueryParam(route: ActivatedRoute) {
		return route.snapshot.queryParamMap.has('createdNewNote');
	}

	public getCreatedNewNoteQueryParam(route: ActivatedRoute) {
		return route.snapshot.queryParamMap.get('createdNewNote');
	}

	public removeCreatedNewNoteQueryParam(router: Router, route: ActivatedRoute) {
		const queryParams = { ...route.snapshot.queryParams };
		delete queryParams['createdNewNote'];
		router.navigate([], { queryParams, replaceUrl: true });
	}

	public redirectToForgotPasswordPage(router: Router) {
		router.navigate(['/potty-world/forgot-password']);
	}

	redirectToScannedDiaryNotes(router: Router) {
		router.navigate(['/potty-world/view-notes'], {
			queryParams: { did: this.localStorageHelper.getScannedDiaryId() }
		});
	}

	redirectToScannedDiaryNote(router: Router) {
		router.navigate([`/potty-world/view-notes`], {
			queryParams: { createdNewNote: true, did: this.localStorageHelper.getScannedDiaryId() }
		});
	}

	redirectToSignUpPageWithClaim(router: Router) {
		router.navigate(['/potty-world/sign-up'], {
			queryParams: { claimGuestbook: true }
		});
	}

	redirectToEnterPinPage(router: Router) {
		router.navigate(['/potty-world/enter-pin']);
	}

	redirectToSignInPage(router: Router) {
		router.navigate(['/potty-world/sign-in'], {
			queryParams: { redirectTo: this.addUrlRedirectQueryParam() }
		});
	}

	redirectToCreateGuestbookPageWithClaim(router: Router) {
		router.navigate(['/potty-world/create-guestbook'], {
			queryParams: { claimGuestbook: true }
		});
	}

	redirectToCreateGuestbookPage(router: Router) {
		router.navigate(['/potty-world/create-guestbook']);
	}

	redirectToSettingsPage(router: Router) {
		router.navigate(['/potty-world/settings']);
	}

	redirectToQrInfo(router: Router) {
		router.navigate(['/potty-world/create-guestbook/intro-qr-info']);
	}

	redirectToQrInfoWithClaim(router: Router) {
		router.navigate(['/potty-world/create-guestbook/intro-qr-info'], {
			queryParams: { claimGuestbook: true }
		});
	}

	redirectToViewQrCodePage(router: Router) {
		router.navigate(['/potty-world/qr-code'])}

	private getOrderOfComponents(): number[] {
		return this.localStorageHelper.getComponentOrder()?.split(',').map(Number);
	}

	// Todo, if we are logged in, pinned into _someone else_, and navigate to your own diary to add a note,
	// then we should add a query param and determine if to go to the diary owners or the scanned
	determineAndRedirectToSuccessfullyPostedNote(router: Router) {
		if (this.signInService.isUserAuthenticated && this.pinAuthenticationService.isAnonUserAuthenticated) {
			this.redirectToScannedDiaryNote(router);
		} else if (this.signInService.isUserAuthenticated && !this.pinAuthenticationService.isAnonUserAuthenticated) {
			this.redirectToDiaryOwnerNote(router);
		}
		else if (!this.signInService.isUserAuthenticated && this.pinAuthenticationService.isAnonUserAuthenticated) {
			this.redirectToScannedDiaryNote(router);
		}
		else {
			// This can be smarter? see who authed last and go to either the sign in or pin page
			this.redirectToSignInPage(router);
		}
	}

	skipButtonPressed(event: any) {
		return event.target.innerHTML.toLowerCase().trim() == 'skip';
	}
}
